import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { T, useLocale } from '@transifex/react';
import {
  Calendar,
  Menu02,
  ChevronLeft,
  ChevronRight,
  Download01,
} from '@untitledui-pro/icons/duotone';
import useMediaQuery from 'use-media-antd-query/es/useMediaQuery';
import { useHistory } from 'src/routing/child-router-factory';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PageHeading } from 'src/components/layout/PageHeading';

import { CollectionDisplay } from '../../../../shared/CollectionDisplay';
import { TableKit } from '../../../../../ui-legacy/table-kit';
import { CollectionCalendar } from './components/calendar';
import {
  getMonthIndex,
  getMonthRangeArray,
  getMonthRangeArrayFromDayjs,
  getYear,
} from '../../../../../utils';

type DateRangePickerProps = {
  initialRange: [Dayjs, Dayjs];
  onChange: (range: [Dayjs, Dayjs]) => void;
};

type UIProps = {
  rangePickerProps: DateRangePickerProps;
  collections: $TSFixMe;
  loading: boolean;
  hasError?: boolean;
  onRetry?: () => void;
};

const YEAR_RANGE: [number, number] = [2018, 2030];

export const UI = ({
  rangePickerProps,
  collections,
  loading,
  hasError,
  onRetry,
}: UIProps) => {
  const [view, setView] = useState<'calendar' | 'list'>('calendar');
  const [value, setValue] = useState<Dayjs>(rangePickerProps.initialRange?.[0]);
  const history = useHistory();

  const { initialRange, onChange } = rangePickerProps;

  // Mirror the controlling prop into local state for the inline calendar.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(initialRange?.[0]);
  }, [initialRange]);

  const veryNarrow = useMediaQuery('(max-width: 420px)');
  const mobile = useMediaQuery('(max-width: 996px)');

  // Force list view on mobile but allow user to switch back on desktop.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (mobile) setView('list');
  }, [mobile]);

  // Read locale so month label re-renders when Transifex flips it.
  useLocale();

  const onPrevMonth = () =>
    onChange(
      getMonthRangeArrayFromDayjs(dayjs(initialRange?.[0]).subtract(1, 'month'))
    );
  const onNextMonth = () =>
    onChange(
      getMonthRangeArrayFromDayjs(dayjs(initialRange?.[0]).add(1, 'month'))
    );

  const currentMonth = dayjs(initialRange?.[0]).month();
  const currentYear = dayjs(initialRange?.[0]).year();
  const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const monthOptions = dayjs
    .localeData()
    .months()
    .map((label, i) => ({ label: capitalize(label), value: i }));
  const yearOptions = Array.from(
    { length: YEAR_RANGE[1] - YEAR_RANGE[0] + 1 },
    (_, i) => YEAR_RANGE[0] + i
  );

  const toolbarShadow = 'shadow-[0_1px_0_0_var(--border-default)]';

  const viewToggle = (
    <div
      role="group"
      aria-label="View mode"
      className={cn(
        'inline-flex h-12 items-center gap-1 rounded-lg border border-border bg-background px-1',
        toolbarShadow
      )}
    >
      <button
        type="button"
        aria-pressed={view === 'calendar'}
        aria-label="Calendar view"
        onClick={() => setView('calendar')}
        className={cn(
          'inline-flex size-10 items-center justify-center rounded-[4px] transition-colors',
          view === 'calendar'
            ? 'bg-[var(--bg-active)] text-[var(--icon-active)]'
            : 'text-[var(--icon-default)] hover:bg-[var(--bg-hover)] hover:text-foreground'
        )}
      >
        <Calendar className="size-6" />
      </button>
      <button
        type="button"
        aria-pressed={view === 'list'}
        aria-label="List view"
        onClick={() => setView('list')}
        className={cn(
          'inline-flex size-10 items-center justify-center rounded-[4px] transition-colors',
          view === 'list'
            ? 'bg-[var(--bg-active)] text-[var(--icon-active)]'
            : 'text-[var(--icon-default)] hover:bg-[var(--bg-hover)] hover:text-foreground'
        )}
      >
        <Menu02 className="size-6" />
      </button>
    </div>
  );

  const calendarMover = (
    <div className="flex items-center justify-between gap-6 md:justify-start md:gap-[31px]">
      <button
        type="button"
        onClick={onPrevMonth}
        aria-label="Previous month"
        className={cn(
          'inline-flex size-12 items-center justify-center rounded-lg border border-border bg-background text-[var(--icon-default)] transition-colors hover:bg-[var(--bg-hover)] hover:text-foreground',
          toolbarShadow
        )}
      >
        <ChevronLeft className="size-6" />
      </button>
      <div className="text-2xl font-semibold leading-[1.2] tracking-[-0.02em] text-foreground whitespace-nowrap">
        {capitalize(dayjs(initialRange?.[0]).format('MMMM YYYY'))}
      </div>
      <button
        type="button"
        onClick={onNextMonth}
        aria-label="Next month"
        className={cn(
          'inline-flex size-12 items-center justify-center rounded-lg border border-border bg-background text-[var(--icon-default)] transition-colors hover:bg-[var(--bg-hover)] hover:text-foreground',
          toolbarShadow
        )}
      >
        <ChevronRight className="size-6" />
      </button>
    </div>
  );

  const yearSelect = (
    <Select
      value={String(currentYear)}
      onValueChange={(v) =>
        onChange(
          getMonthRangeArray(getMonthIndex(initialRange?.[0]), parseInt(v, 10))
        )
      }
    >
      <SelectTrigger
        aria-label="Year"
        className={cn(
          'h-12 w-full rounded-lg border-border bg-background px-4 text-base font-medium text-[var(--text-secondary)] data-[size=default]:h-12 data-[placeholder]:text-[var(--text-secondary)] md:w-32',
          toolbarShadow
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {yearOptions.map((y) => (
          <SelectItem key={y} value={String(y)}>
            {y}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const monthSelect = (
    <Select
      value={String(currentMonth)}
      onValueChange={(v) =>
        onChange(
          getMonthRangeArray(
            parseInt(v, 10),
            Number(getYear(initialRange?.[0]))
          )
        )
      }
    >
      <SelectTrigger
        aria-label="Month"
        className={cn(
          'h-12 w-full rounded-lg border-border bg-background px-4 text-base font-medium text-[var(--text-secondary)] data-[size=default]:h-12 data-[placeholder]:text-[var(--text-secondary)] md:w-32',
          toolbarShadow
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {monthOptions.map((m) => (
          <SelectItem key={m.value} value={String(m.value)}>
            {m.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const downloadButton = (
    <button
      type="button"
      onClick={() => history.push('/collections/download')}
      className={cn(
        'col-span-2 inline-flex h-12 w-full items-center justify-center gap-4 rounded-lg border border-border bg-background px-4 text-base font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-foreground md:w-auto md:justify-start',
        toolbarShadow
      )}
    >
      <Download01 className="size-6" />
      <T _str="Download" />
    </button>
  );

  const dateFormat = veryNarrow ? 'MMM D, YYYY' : 'LL';

  return (
    <div className="wh-ui flex flex-col">
      <div className="mb-8">
        <PageHeading
          title={<T _str="Schedule" />}
          subtitle={
            <T _str="See upcoming collection dates for your property." />
          }
        />
      </div>
      <div className="mb-8 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-between">
        {calendarMover}
        <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
          {yearSelect}
          {monthSelect}
          {downloadButton}
          {/* Calendar view (and thus the toggle) is desktop-only — view is
              forced to list on mobile, so the toggle would be non-functional. */}
          {!mobile && viewToggle}
        </div>
      </div>
      {loading && <TableKit.Spinner />}
      {hasError && (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm text-[var(--text-secondary)]">
            <T _str="We couldn't load your collection schedule. The server took too long to respond." />
          </p>
          {onRetry && (
            <Button variant="outline" onClick={onRetry}>
              <T _str="Try again" />
            </Button>
          )}
        </div>
      )}
      {!loading &&
        !hasError &&
        !Object.keys(collections).length &&
        view === 'list' && <TableKit.NoData />}
      {!loading &&
        view === 'list' &&
        Object.values(collections)?.map((collection: $TSFixMe) => (
          <CollectionDisplay
            key={collection?.[0]?.date}
            containers={collection}
            date={dayjs(collection?.[0]?.date).format(dateFormat)}
          />
        ))}
      {!loading && view === 'calendar' && (
        <CollectionCalendar
          data={collections}
          onChange={onChange}
          value={value}
          setValue={setValue}
        />
      )}
    </div>
  );
};
